import { isEqual } from 'lodash';
import { computed, ref, watch } from 'vue';

import useAuth from '@/composables/useAuth';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { WEBHOOK_EVENT_TYPES } from '@/constants/webhooks';
import {
  useFeatureFlagStore,
  useFlashesStore,
  useIntegrationProfileStore,
  useWebhooksStore,
} from '@/stores';
import { MinimalIntegrationProfile } from '@/types/integration_profiles';
import { WebhookEventType } from '@/types/webhooks';
import {
  fillStandardPopulationSettings,
  removeConfigType,
} from '@/utils/integrationProfileSettings';

export default function useWebhookSettings(webhookId: string) {
  const { hasPermission } = useAuth();
  const webhooksStore = useWebhooksStore();
  const integrationProfileStore = useIntegrationProfileStore();
  const flashesStore = useFlashesStore();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const hasNNAEvent = computed(
    () =>
      hasFeatureFlag(FEATURE_FLAG.TEMP_GENPIPE_GREENFIELDS) &&
      hasFeatureFlag(FEATURE_FLAG.TEMP_GREENFIELD_DEAL_WEBHOOKS),
  );

  // Loading states
  const isSaving = ref(false);
  const isTesting = ref(false);
  const isWebhookEnabled = ref(false);
  const isNetNewAccountsEnabled = ref(false);

  // Both needed for comparisons (hasUnsavedChanges, saveWebhook)
  const localProfileSettings = ref<MinimalIntegrationProfile | null>(null);
  const originalWebhookState = ref<{
    status: string;
    enabled: boolean;
    isNetNewAccountsEnabled: boolean;
  } | null>(null);

  // Computed properties
  const currentWebhook = computed(() =>
    webhooksStore.webhooks.find((webhook) => webhook.id === webhookId),
  );
  const isWebhookTested = computed(
    () => currentWebhook.value?.status !== 'untested',
  );
  const canManageIntegrations = computed(() =>
    hasPermission('manage:integrations'),
  );

  // Get the webhook's integration profile
  const webhookIntegrationProfile = computed(() => {
    if (!currentWebhook.value?.filters?.[0]?.profile_id) return null;

    const profileId = currentWebhook.value.filters[0].profile_id;
    return integrationProfileStore.integrationProfiles.find(
      (profile) => profile.profile_id === profileId,
    );
  });

  // Webhook profile settings for IntegrationSettings component
  const webhookProfileSettings = computed<MinimalIntegrationProfile>(() => {
    if (!webhookIntegrationProfile.value) {
      // Default settings for new webhook
      return {
        population_settings: [],
        standard_population_settings: fillStandardPopulationSettings(),
        exclude_partnerships_created_since: null,
      };
    }

    return {
      population_settings:
        webhookIntegrationProfile.value.population_settings || [],
      standard_population_settings: fillStandardPopulationSettings(
        webhookIntegrationProfile.value.standard_population_settings,
      ),
      exclude_partnerships_created_since:
        webhookIntegrationProfile.value.exclude_partnerships_created_since,
    };
  });

  const hasUnsavedChanges = computed(() => {
    if (!originalWebhookState.value || !currentWebhook.value) return false;

    const toggleChanged =
      originalWebhookState.value.enabled !== isWebhookEnabled.value;

    const nnaChanged =
      originalWebhookState.value.isNetNewAccountsEnabled !==
      isNetNewAccountsEnabled.value;

    const storeSettings = webhookProfileSettings.value;
    const currentSettings =
      localProfileSettings.value || webhookProfileSettings.value;
    const normalizedCurrent = removeConfigType(currentSettings);
    const normalizedStore = removeConfigType(storeSettings);
    const profileSettingsChanged = !isEqual(normalizedCurrent, normalizedStore);

    return toggleChanged || nnaChanged || profileSettingsChanged;
  });

  const canSave = computed(
    () =>
      hasUnsavedChanges.value && !isSaving.value && canManageIntegrations.value,
  );

  watch(
    () => currentWebhook.value?.status,
    (status) => {
      if (!status || !originalWebhookState.value) return;
      isWebhookEnabled.value = status === 'active';
      originalWebhookState.value = {
        ...originalWebhookState.value,
        status,
        enabled: status === 'active',
      };
    },
  );

  // Methods
  async function initializeWebhookState() {
    await integrationProfileStore.readySync;
    if (currentWebhook.value) {
      isWebhookEnabled.value = currentWebhook.value.status === 'active';
      isNetNewAccountsEnabled.value = (
        currentWebhook.value.event_types as WebhookEventType[]
      ).includes(WEBHOOK_EVENT_TYPES.NET_NEW_ACCOUNTS as WebhookEventType);

      originalWebhookState.value = {
        status: currentWebhook.value.status,
        enabled: isWebhookEnabled.value,
        isNetNewAccountsEnabled: isNetNewAccountsEnabled.value,
      };
    }
  }

  async function handleSave() {
    if (!canSave.value || !currentWebhook.value) return;
    isSaving.value = true;

    let hasAnySuccess = false;
    let hasAnyFailure = false;

    try {
      const statusChanged =
        originalWebhookState.value?.enabled !== isWebhookEnabled.value;
      const nnaChanged =
        originalWebhookState.value?.isNetNewAccountsEnabled !==
        isNetNewAccountsEnabled.value;

      if (statusChanged) {
        const newStatus = isWebhookEnabled.value ? 'active' : 'paused';
        const result = await webhooksStore.updateWebhook(webhookId, {
          status: newStatus,
        });

        if (result && originalWebhookState.value) {
          originalWebhookState.value.status = newStatus;
          originalWebhookState.value.enabled = isWebhookEnabled.value;
          hasAnySuccess = true;
        } else {
          hasAnyFailure = true;
        }
      }

      if (nnaChanged) {
        const currentEventTypes = (
          currentWebhook.value.event_types as WebhookEventType[]
        ).filter(
          (type) =>
            type !== (WEBHOOK_EVENT_TYPES.NET_NEW_ACCOUNTS as WebhookEventType),
        );

        if (isNetNewAccountsEnabled.value) {
          currentEventTypes.push(
            WEBHOOK_EVENT_TYPES.NET_NEW_ACCOUNTS as WebhookEventType,
          );
        }

        const configResult = await webhooksStore.editWebhookConfig(webhookId, {
          name: currentWebhook.value.name,
          target_url: currentWebhook.value.target_url,
          event_types: currentEventTypes,
        });

        if (configResult && originalWebhookState.value) {
          originalWebhookState.value.isNetNewAccountsEnabled =
            isNetNewAccountsEnabled.value;
          hasAnySuccess = true;
        } else {
          hasAnyFailure = true;
        }
      }

      if (webhookIntegrationProfile.value) {
        try {
          const settingsToSave =
            localProfileSettings.value || webhookProfileSettings.value;
          const settingsForApi = removeConfigType(settingsToSave);
          await integrationProfileStore.putIntegrationProfileSettings(
            webhookIntegrationProfile.value.profile_id,
            settingsForApi,
          );

          await integrationProfileStore.refreshIntegrationProfileStore();
          localProfileSettings.value = null;
          hasAnySuccess = true;
        } catch {
          hasAnyFailure = true;
          flashesStore.addErrorFlash({
            message: 'Failed to save integration profile settings',
          });
        }
      } else {
        localProfileSettings.value = null;
      }

      if (hasAnySuccess && !hasAnyFailure) {
        flashesStore.addSuccessFlash({
          message: 'Webhook settings saved successfully',
          description: 'Your webhook configuration has been saved.',
        });
      }
    } catch (error) {
      flashesStore.addErrorFlash({
        message: 'Failed to save webhook settings',
      });
    } finally {
      isSaving.value = false;
    }
  }

  async function handleDeleteWebhook() {
    const success = await webhooksStore.deleteWebhook(webhookId);
    if (!success) return false;

    const profileId = currentWebhook.value?.filters?.[0]?.profile_id;
    if (profileId) {
      integrationProfileStore.deleteIntegrationProfileInStore(profileId);
      flashesStore.addSuccessFlash({ message: 'Webhook deleted successfully' });
    }
    return true;
  }

  async function handleTestWebhook() {
    isTesting.value = true;
    await webhooksStore.testWebhook(webhookId);
    isTesting.value = false;
  }

  function handleDiscardChanges() {
    if (originalWebhookState.value && currentWebhook.value) {
      isWebhookEnabled.value = originalWebhookState.value.enabled;
      isNetNewAccountsEnabled.value =
        originalWebhookState.value.isNetNewAccountsEnabled;
      localProfileSettings.value = null;
    }
  }

  function onWebhookSettingsUpdated(payload: MinimalIntegrationProfile) {
    localProfileSettings.value = payload;
  }

  function onUpdateExcludePartnershipsCreatedSince(isChecked: boolean) {
    const currentSettings =
      localProfileSettings.value || webhookProfileSettings.value;
    const originalSettings = webhookProfileSettings.value;

    localProfileSettings.value = {
      ...currentSettings,
      exclude_partnerships_created_since: isChecked
        ? null
        : originalSettings?.exclude_partnerships_created_since ||
          new Date().toISOString(),
    };
  }

  // Utility methods
  function copyWebhookUrl() {
    if (currentWebhook.value?.target_url) {
      navigator.clipboard.writeText(currentWebhook.value.target_url);
      flashesStore.addSuccessFlash({
        message: 'Webhook URL copied to clipboard',
      });
    }
  }

  return {
    // State
    currentWebhook,
    webhookIntegrationProfile,
    webhookProfileSettings,
    localProfileSettings,
    isWebhookEnabled,
    isNetNewAccountsEnabled,

    // Computed
    isWebhookTested,
    hasUnsavedChanges,
    canManageIntegrations,
    canSave,
    hasNNAEvent,

    // Loading states
    isSaving,
    isTesting,

    // Methods
    initializeWebhookState,
    saveWebhook: handleSave,
    deleteWebhook: handleDeleteWebhook,
    testWebhook: handleTestWebhook,
    discardChanges: handleDiscardChanges,
    copyWebhookUrl,
    onWebhookSettingsUpdated,
    onUpdateExcludePartnershipsCreatedSince,
  };
}
